import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import EloBadge from "../common/EloBadge.js";
import BarChartIcon from "@mui/icons-material/BarChart";
import Avatar from "@mui/material/Avatar";
import FlagIcon from '@mui/icons-material/Flag';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { format } from 'date-fns';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import StarIcon from '@mui/icons-material/Star';
import Chip from '@mui/material/Chip';
import { Link } from "react-router-dom";
import utils from "../../universal/utils";
import {
  pickAvatarUpdatedAtMs,
  resolveAvatarSrc,
} from "../../utils/avatarUrl";
import { buildProfileExternalLinks } from "./externalProfileLinks";
import TrendIndicator from "../common/TrendIndicator";
import Rating3Badge from "../common/Rating3Badge";
import { FOUNDER_BADGE, FOUNDER_TOOLTIP_TEXT } from "../../constants/founder";

const basePath = utils.getEdgeBasePath();

const PROFILE_BADGE_HEIGHT = "1.85rem";

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    position: "relative",
    width: "100%",
    backgroundColor: "#1C1E20",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "2rem",
    padding: "2.5rem",
    boxSizing: "border-box",  
    overflow: "hidden",
    border: "1px solid rgba(232, 163, 61, 0.15)",
  },
  containerContent: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  containerProfileStats: {
    display: "grid",
    gridTemplateColumns: "minmax(10rem, 1fr) minmax(0, 2fr)", 
    gridTemplateRows: "auto auto",
    width: "100%",
    height: "100%",
    columnGap: "1.4rem",
  },
  avatar: {
    width: "9.2rem",
    height: "9.2rem",
    borderRadius: "4px",
  },
  avatarColumn: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
  },
  avatarStage: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "1.1rem",
  },
  containerMainInfo: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "auto auto",
    position: "relative",
    width: "100%",
    rowGap: "1rem",
    columnGap: "1.5rem",
  },
  countryLabel: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    width: "100%",
    height: "3.5rem",
    padding: "0 1.25rem",
    backgroundColor: "#2A2F34",
    borderRadius: "4px",
    gap: "0.5rem",
    boxSizing: "border-box",
  },
  countryFlag: {
    position: "relative",
    width: "1.5rem",
    height: "1.5rem",
  },
  countryText: {
    fontSize: "20px",
    color: "#ffffff",
    fontWeight: "525",
    textAlign: "start",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    maxWidth: "5.9rem",
  },
  labelPlayerElo: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "3.5rem",
    padding: "0 1.25rem",
    backgroundColor: "#2A2F34",
    borderRadius: "4px",
    boxSizing: "border-box",
  },
  labelJoinedDate: {
    gridColumn: "1 / span 2",
    gridRow: "2",
    width: "100%", 
    height: "6rem",
    padding: "1rem 1.25rem",
    backgroundColor: "#2A2F34",
    borderRadius: "4px",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "flex-start",
    gap: "0.75rem",
  },
  containerSecondaryInfo: {
    gridColumn: "1 / span 2",
    gridRow: "2",
    display: 'grid',
    gridTemplateColumns: '10rem 1fr',
    gridTemplateRows: 'auto',
    gap: "1.5rem",
    marginTop: "1rem",
  },
  playerName: {
    fontSize: "30px", 
    textAlign: "center", 
    color: "#ffffff", 
    fontWeight: "600", 
    lineHeight: 1.1,
    marginTop: "-0.4rem",
    marginBottom: "0.6rem",
    width: "100%",
    maxWidth: "12rem",
  },
  reputationBadge: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "0.5rem",
    marginBottom: "0.75rem",
    cursor: "pointer",
    "&:hover": {
      opacity: 0.85,
    },
  },
  reputationChip: {
    cursor: "pointer",
    "&:hover": {
      opacity: 0.92,
    },
  },
  founderAvatarBadge: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translate(-50%, 0)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.25rem",
    minHeight: "1.7rem",
    minWidth: "3.1rem",
    backgroundColor: "rgba(18, 18, 20, 0.96)",
    border: `1px solid ${FOUNDER_BADGE.borderColor}`,
    borderRadius: "999px",
    padding: "0.2rem 0.55rem",
    whiteSpace: "nowrap",
    zIndex: 1,
    cursor: "default",
  },
  founderAvatarIcon: {
    color: FOUNDER_BADGE.color,
    fontSize: "0.9rem",
  },
  founderAvatarText: {
    color: FOUNDER_BADGE.color,
    fontFamily: "'Oxanium', sans-serif",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.3px",
    lineHeight: 1,
  },
  operationalBadgeRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.55rem",
    width: "100%",
    minHeight: PROFILE_BADGE_HEIGHT,
    marginBottom: "0.9rem",
  },
  profileStatusChip: {
    height: PROFILE_BADGE_HEIGHT,
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.45px",
    '& .MuiChip-label': {
      display: "flex",
      alignItems: "center",
      padding: "0 0.7rem",
      lineHeight: 1,
    },
    '& .MuiChip-icon': {
      marginLeft: "0.55rem",
      marginRight: "-0.2rem",
      fontSize: "0.95rem",
    },
  },
  publicBanCallout: {
    width: "100%",
    maxWidth: "16rem",
    backgroundColor: "rgba(165, 42, 42, 0.15)",
    border: "1px solid rgba(244, 67, 54, 0.45)",
    borderRadius: "4px",
    padding: "0.75rem 0.9rem",
    boxSizing: "border-box",
    marginBottom: "0.75rem",
  },
  publicBanTitle: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: "#ff8a80",
    marginBottom: "0.25rem",
    textAlign: "center",
  },
  publicBanReason: {
    fontSize: "0.8rem",
    color: "#ffffff",
    lineHeight: 1.4,
    textAlign: "center",
    marginBottom: "0.35rem",
  },
  publicBanUntil: {
    fontSize: "0.72rem",
    color: "#d0d0d0",
    lineHeight: 1.4,
    textAlign: "center",
  },
  formatContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
  },
  labelSteamFaceit: {
    gridColumn: '1',
    gridRow: '2',
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    gap: "1rem",
    width: "100%",
    height: "4rem",
    backgroundColor: "#2A2F34",
    borderRadius: "4px",
  },
  steamProfileLinkButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "2.8rem",
    height: "2.8rem",
    '&:hover': {
      opacity: 0.8,
    },
  },
  steamIcon: {
    width: "2.8rem",
    height: "2.8rem",
  },
  faceitProfileLinkButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#ffffff",
    width: "2.6rem",
    height: "2.6rem",
    borderRadius: "4px",
    '&:hover': {
      opacity: 0.8,
    },
  },
  faceitIcon: {
    width: "1.8rem",
    height: "1.8rem",
  },
  externalProfileLinkDisabled: {
    opacity: 0.45,
  },
  labelPlayerWinRate: {
    gridColumn: '2',
    gridRow: '1 / span 2',
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    height: "5.5rem",
    backgroundColor: "#2A2F34",
    borderRadius: "4px",
    marginTop: "0.5rem",
    padding: "0.75rem 0",
  },
  containerMatches: {
    width: "6rem",
    height: "100%",
    borderRight: "0.3px solid #797979",
    borderRadius: "4px",
    padding: "0 1.25rem",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "0.25rem",
  },
  textMatches: {
    fontSize: "13px", 
    color: "#797979", 
    fontWeight: "500", 
  },
  subText: {
    fontSize: "18px", 
    color: "#ffffff", 
    fontWeight: "600", 
  },
  containerWinrate: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    textAlign: "start",
    width: "100%",
    height: "100%",
    padding: "0 1.25rem",
    gap: "1.5rem",
  },
  textWinLoss: {
    fontSize: "13px", 
    color: "#797979", 
    fontWeight: "500", 
  },
  formatCircularProgress: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  boxCircularProgress: {
    position: "relative", 
    display: "inline-flex",
  },
  circularProgress: {
    '& .MuiCircularProgress-circle': {
      strokeLinecap: 'round', 
    },
  },
  tooltipContent: {
    backgroundColor: "#1C1E20",
    padding: "0.3rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid #6c757d",
    color: "#ffffff",
    fontSize: "12px",
    marginBottom: "0.5rem",
  },
  containerStatsBar: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: "1rem",
    width: "100%",
    marginTop: "1rem",
  },
  statCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#2A2F34",
    borderRadius: "4px",
    padding: "0.75rem 1rem",
    boxSizing: "border-box",
  },
  statLabel: {
    fontSize: "0.75rem",
    color: "#999999",
    fontWeight: 500,
    marginBottom: "0.25rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statValue: {
    fontSize: "1.1rem",
    color: "#ffffff",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
  },
  clickableStatCard: {
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#343A40",
    },
  },
  reputationDetailsContainer: {
    gridColumn: "1 / -1",
    backgroundColor: "#1C1E20",
    borderRadius: "4px",
    padding: "2rem",
    marginTop: "1rem",
    border: "1px solid rgba(232, 163, 61, 0.2)",
  },
  dimensionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "2rem",
    marginBottom: "1.5rem",
    '@media (max-width: 1200px)': {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    '@media (max-width: 768px)': {
      gridTemplateColumns: "1fr",
    },
  },
  dimensionCard: {
    backgroundColor: "#2A2F34",
    borderRadius: "4px",
    padding: "1rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  dimensionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dimensionLabel: {
    fontSize: "0.9rem",
    color: "#ffffff",
    fontWeight: 500,
  },
  dimensionScore: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#E8A33D",
  },
  dimensionBar: {
    width: "100%",
    height: "6px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: "3px",
    overflow: "hidden",
  },
  dimensionBarFill: {
    height: "100%",
    backgroundColor: "#E8A33D",
    borderRadius: "3px",
    transition: "width 0.3s ease",
  },
  eventsSection: {
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
    paddingTop: "1rem",
  },
  eventsTitle: {
    fontSize: "0.85rem",
    color: "#999999",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "0.75rem",
    fontWeight: 500,
  },
  eventsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "0.75rem",
  },
  eventCard: {
    backgroundColor: "#2A2F34",
    borderRadius: "6px",
    padding: "0.75rem",
    textAlign: "center",
  },
  eventCount: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#E8A33D",
    marginBottom: "0.25rem",
  },
  eventLabel: {
    fontSize: "0.7rem",
    color: "#999999",
    textTransform: "uppercase",
  },
  rankBadge: {
    fontSize: "0.9rem",
    fontWeight: 700,
    padding: "0.15rem 0.5rem",
    borderRadius: "6px",
    backgroundColor: "rgba(232, 163, 61, 0.2)",
    color: "#E8A33D",
    border: "1px solid #E8A33D",
  },
  containerLayoutProfileInfo: {
    height: "100%",  
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "stretch",
    flex: 1,  
  },
  containerLayoutProfileActions: {
    position: "relative",
    width: "18.5rem",
    borderRadius: "4px",
    backgroundColor: "#2A2F34",
    padding: "1.5rem",
    boxSizing: "border-box",
    display: "flex",  
    flexDirection: "column",
    alignSelf: "flex-start",
    minHeight: 0, 
  },
  containerMatchWidget: {
    width: "100%",
    display: 'flex',
  },
  statusWidget: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.5rem',
  },
  offlineDiv: {
    width: '100%',
    minHeight: '10rem',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e0e2e410',
    padding: '1rem',
    boxSizing: 'border-box',
  },
  statusDetail: {
    fontSize: '14px',
    marginTop: '0.5rem',
  },
  inGameDiv: {
    width: '100%',
    minHeight: '10rem',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    border: '1px solid rgba(76, 175, 80, 0.35)',
    padding: '1rem',
    boxSizing: 'border-box',
    gap: '0.75rem',
  },
  onlineDiv: {
    width: '100%',
    minHeight: '10rem',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#38b00015',
    padding: '1rem',
    boxSizing: 'border-box',
  },
  containerActions: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    flex: 0,
    marginTop: '1rem',
  },
  actionButtonContainer: {
    width: "100%",
    marginTop: "0.6rem",  
  },
  dualActionRow: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.5rem",
  },
  actionButton: {
    width: "100%",
    borderRadius: "4px",
    textTransform: "none",
    fontWeight: 525,
    padding: "0.5rem",
  },
  btnAddFriend: {
    color: '#ffffff',
    backgroundColor: '#E8A33D',
    '&:hover': {
      backgroundColor: '#F4B752',
    },
  },
  btnRequestPending: {
    color: '#FFFFFF',
    backgroundColor: '#5a5f66',
    '&:hover': {
      backgroundColor: '#545960',
    },
  },
  btnCancelRequest: {
    position: 'absolute',
    right: '0.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
    minWidth: 'auto',
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#FFFFFF',
    padding: '0',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
    '& .MuiButton-label': {
      fontSize: '0.875rem',
    },
  },
  btnSettings: {
    color: '#ffffff',
    backgroundColor: 'rgba(224, 226, 228, 0.5)',
    '&:hover': {
      backgroundColor: 'rgba(224, 226, 228, 0.3)',
    },
  },
  btnAlreadyFriend: {
    color: '#ffffff',
    backgroundColor: '#38b000',
    '&:hover': {
      backgroundColor: '#38b000',
      cursor: 'default',
    },
  },
  btnAcceptFriend: {
    color: '#ffffff',
    backgroundColor: '#38b000',
    '&:hover': {
      backgroundColor: '#32a000',
    },
  },
  btnDismissFriend: {
    color: '#ffffff',
    backgroundColor: '#666c73',
    '&:hover': {
      backgroundColor: '#5b6168',
    },
  },
  btnUnfriend: {
    color: '#ffffff',
    backgroundColor: '#A52A2A',
    '&:hover': {
      backgroundColor: '#8B0000',
    },
  },
  btnReport: {
    color: '#ffffff',
    backgroundColor: '#A52A2A',
    '&:hover': {
      backgroundColor: '#8B0000',
    },
  },
  btnMatchroom: {
    color: '#ffffff',
    backgroundColor: '#38b000',
    '&:hover': {
      backgroundColor: '#32a000',
    },
  },
  statusDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    marginRight: '0.5rem',
  },
  statusText: {
    fontSize: '18px',
    fontWeight: '525',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
  },
  '@media (max-width: 768px)': {
    containerProfileStats: {
      gridTemplateColumns: "1fr",  
    },
    avatarStage: {
      paddingTop: "1rem",
    },
    avatar: {
      width: "7rem",  
      height: "7rem",
      borderRadius: "4px",
    },
    founderAvatarBadge: {
      minHeight: "1.55rem",
      padding: "0.15rem 0.5rem",
    },
    founderAvatarText: {
      fontSize: "0.65rem",
    },
    containerStatsBar: {
      gridTemplateColumns: "1fr",
      gap: "0.75rem",
    },
  },
}));

export default function ProfileCard({
  profileData, 
  matchSummary,
  recentStats, // Last 3 months stats (ADR, K/D, etc.)
  statsTrends, // Trend data for stats (optional)
  isOwnProfile = true, 
  currentUserAvatarSource = null,
  playerStatus = 'inLobby',
  sendRequestFriend,
  cancelFriendRequest,
  acceptFriendRequest,
  dismissFriendRequest,
  unfriend,
  friendRequestSuccess = null,
  friendRequestError = null,
  isFriendRequestSubmitting = false,
  friendshipStatus = 'none'
}) {
  const classes = useStyles();
  const [reputation, setReputation] = useState(null);
  const [reputationDetails, setReputationDetails] = useState(null);
  const [showReputationDetails, setShowReputationDetails] = useState(false);
  const [countryRank, setCountryRank] = useState(null);
  const [reportsSummary, setReportsSummary] = useState(null);

  // Extract join date from MongoDB ObjectId or use explicit field
  const getJoinDate = () => {
    // Check for explicit 'joined' field (Unix timestamp in seconds)
    if (profileData?.joined) {
      return format(new Date(profileData.joined * 1000), 'dd MMM, yyyy');
    }
    
    // Extract timestamp from MongoDB _id (first 8 hex chars are timestamp)
    if (profileData?._id) {
      try {
        const timestamp = parseInt(profileData._id.substring(0, 8), 16);
        return format(new Date(timestamp * 1000), 'dd MMM, yyyy');
      } catch {
        return '-';
      }
    }
    
    return '-';
  };

  const joinedDate = getJoinDate();

  // Fetch reputation badge and reports
  useEffect(() => {
    // Get steamId from any available field
    const steamId = profileData?.steamId64 || profileData?.steamId || profileData?.steam64Id;
    
    if (steamId) {
      fetchReputation(steamId);
      fetchCountryRank(steamId);
      fetchReportsSummary(steamId);
    }
  }, [profileData?.steamId64, profileData?.steamId, profileData?.steam64Id]);

  const fetchReputation = async (steamId) => {
    try {
      const response = await fetch(`${basePath}/api/v1/reputation/${steamId}`, {
        credentials: "include",
      });
      const result = await response.json();
      if (result.ok) {
        setReputation(result.badge);
      }
    } catch {
      setReputation(null);
    }
  };

  const fetchReputationDetails = async (steamId) => {
    try {
      const response = await fetch(`${basePath}/api/v1/reputation/${steamId}?detailed=true`, {
        credentials: "include",
      });
      const result = await response.json();
      if (result.ok && result.details) {
        setReputationDetails(result.details);
      }
    } catch {
      setReputationDetails(null);
    }
  };

  const toggleReputationDetails = () => {
    if (!showReputationDetails && !reputationDetails && profileData?.steamId64) {
      // Fetch details on first expand
      const steamId = profileData.steamId64 || profileData.steamId || profileData.steam64Id;
      fetchReputationDetails(steamId);
    }
    setShowReputationDetails(!showReputationDetails);
  };

  const fetchCountryRank = async (steamId) => {
    try {
      // TODO: Implement backend endpoint /api/v1/leaderboard/country-rank/:steamId
      // For now, leaving as null until backend is ready
      setCountryRank(null);
    } catch {
      setCountryRank(null);
    }
  };

  const fetchReportsSummary = async (steamId) => {
    try {
      const response = await fetch(`${basePath}/api/v1/reputation/${steamId}/reports-summary`, {
        credentials: "include",
      });
      const result = await response.json();
      if (result.ok) {
        setReportsSummary(result.summary);
      } else {
        setReportsSummary({ totalReports: 0 });
      }
    } catch {
      setReportsSummary({ totalReports: 0 });
    }
  };

  const avatarVersion = pickAvatarUpdatedAtMs(
    isOwnProfile ? currentUserAvatarSource : null,
    profileData
  );
  const avatarSrc = resolveAvatarSrc({
    basePath,
    picture: profileData?.picture,
    userId: profileData?.userId || profileData?._id,
    avatarUpdatedAtMs: avatarVersion,
    hasCustomAvatar: !!profileData?.avatar,
  });
  
  function getLastSeenLabel(lastOnlineUpdate) {
    if (!lastOnlineUpdate) return 'Unknown';
    
    const ts = new Date(lastOnlineUpdate).getTime();
    
    if (Number.isNaN(ts)) return 'Unknown';
    
    const diffMs = Date.now() - ts;
    if (diffMs < 0) return 'Last seen 1 minute ago';
    
    const totalMinutes = Math.floor(diffMs / 60000);
    if (totalMinutes < 60) {
      const m = Math.max(1, totalMinutes);
      return `Last seen ${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
    }

    const totalHours = Math.floor(totalMinutes / 60);
    if (totalHours < 24) {
      const h = Math.max(1, totalHours);
      return `Last seen ${h} ${h === 1 ? 'hour' : 'hours'} ago`;
    }

    const days = Math.max(1, Math.floor(totalHours / 24));
    return `Last seen ${days} ${days === 1 ? 'day' : 'days'} ago`;
  }

  const steamId = profileData?.steamId64 || profileData?.steamId || profileData?.steam64Id || null;
  const lastSeen = getLastSeenLabel(profileData?.lastOnlineUpdate);
  const effectiveFriendshipStatus =
    friendshipStatus === 'friends' ||
    friendshipStatus === 'pending_outgoing' ||
    friendshipStatus === 'pending_incoming'
      ? friendshipStatus
      : 'none';
  const externalLinks = buildProfileExternalLinks(profileData);
  // Didit KYC only. accountStatus "verified" is Ouro account review, not the biometric badge.
  const isBiometricVerified = profileData?.kyc?.badgeEligible === true;

  try {
    return (
      <div className={classes.cardContainer}>
      <div className={classes.containerLayoutProfileInfo}>
        <div className={classes.containerContent}>
          <div className={classes.containerProfileStats}>
            <div className={classes.avatarColumn}>
              <div className={classes.avatarStage}>
                <Avatar className={classes.avatar} src={avatarSrc} variant="square" />
                {profileData.founderBadge?.granted && (
                  <Tooltip title={FOUNDER_TOOLTIP_TEXT} placement="top" arrow>
                    <div className={classes.founderAvatarBadge}>
                      <StarIcon className={classes.founderAvatarIcon} />
                      <Typography className={classes.founderAvatarText} component="span" style={{ fontFamily: "'Oxanium', sans-serif" }}>
                        #{profileData.founderBadge.number || ""}
                      </Typography>
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
            <div className={classes.containerMainInfo}>
              <div className={classes.countryLabel}>
                {profileData.flagImageUrl && (
                  <Tooltip
                    title={profileData.countryDisplayName || profileData.country}
                    placement="top"
                    classes={{
                      tooltip: classes.tooltipContent,
                  }}>
                    <img src={profileData.flagImageUrl} className={classes.countryFlag}/>
                  </Tooltip>
                )}
                {(profileData.countryDisplayName || profileData.country) &&
                !/^(unknown)$/i.test(
                  String(profileData.countryDisplayName || profileData.country).trim()
                ) ? (
                <Typography className={classes.countryText}>
                  {profileData.countryDisplayName || profileData.country}
                </Typography>
                ) : null}
              </div>

              <div className={classes.labelPlayerElo}>
                <EloBadge value={profileData.elo} size="large" />
              </div>

              <div className={classes.labelJoinedDate}>
                  <DateRangeIcon style={{ width: "1.6rem", height: "1.6rem", marginTop: "0.8rem", color: "#ffffff", }}/>
                  <div style={{ textAlign: "start", }}>
                    <Typography style={{ fontSize: "20px", color: "#ffffff", fontWeight: "525", lineHeight: "1.2", marginTop: "0.4rem", }}>Joined</Typography>
                    <Typography style={{ fontSize: "20px", color: "#ffffff", fontWeight: "525", lineHeight: "1.4", }}>{joinedDate}</Typography>
                  </div>
              </div>
            </div>

            <div className={classes.containerSecondaryInfo}>
              <div className={classes.formatContainer}>
                <Typography className={classes.playerName}>{profileData.nickname}</Typography>
                
                {/* Operational Badges: Membership + Reputation (horizontal row) */}
                <div className={classes.operationalBadgeRow}>
                  {(profileData.membershipTier || profileData.membership?.tier) && (profileData.membershipTier || profileData.membership?.tier) !== "free" && (
                    <Chip
                      label={((profileData.membershipTier || profileData.membership?.tier) === "pro") ? "PRO" : "PREMIUM"}
                      className={classes.profileStatusChip}
                      size="small"
                      style={{
                        backgroundColor: ((profileData.membershipTier || profileData.membership?.tier) === "pro")
                          ? "rgba(255, 215, 0, 0.15)"
                          : "rgba(232, 163, 61, 0.15)",
                        color: ((profileData.membershipTier || profileData.membership?.tier) === "pro") ? "#FFD700" : "#E8A33D",
                        border: `1px solid ${((profileData.membershipTier || profileData.membership?.tier) === "pro") ? "#FFD700" : "#E8A33D"}`,
                        order: 2,
                        height: PROFILE_BADGE_HEIGHT,
                      }}
                    />
                  )}
                  {isBiometricVerified && (
                    <Tooltip
                      title="This player completed biometric identity verification."
                      placement="top"
                      classes={{ tooltip: classes.tooltipContent }}
                    >
                      <Chip
                        icon={<FingerprintIcon style={{ color: "#38b000", fontSize: "0.95rem" }} />}
                        label="Verified"
                        className={classes.profileStatusChip}
                        size="small"
                        style={{
                          backgroundColor: "rgba(56, 176, 0, 0.14)",
                          color: "#38b000",
                          border: "1px solid rgba(56, 176, 0, 0.75)",
                          order: 1,
                          height: PROFILE_BADGE_HEIGHT,
                        }}
                      />
                    </Tooltip>
                  )}
                  {reputation && (
                    <Tooltip 
                      title="Click to view detailed reputation breakdown"
                      placement="top"
                      classes={{ tooltip: classes.tooltipContent }}
                    >
                      <Chip
                        icon={<VerifiedUserIcon style={{ color: reputation.tierColor, fontSize: "0.95rem" }} />}
                        label={reputation.tierLabel}
                        className={`${classes.profileStatusChip} ${classes.reputationChip}`}
                        onClick={toggleReputationDetails}
                        style={{
                          backgroundColor: `${reputation.tierColor}25`,
                          border: `1.5px solid ${reputation.tierColor}`,
                          color: reputation.tierColor,
                          height: PROFILE_BADGE_HEIGHT,
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  )}
                  {(profileData.gender === "male" ||
                    profileData.gender === "female") && (
                    <Chip
                      label={profileData.gender === "male" ? "Male" : "Female"}
                      className={classes.profileStatusChip}
                      size="small"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.07)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.2)",
                        height: PROFILE_BADGE_HEIGHT,
                      }}
                    />
                  )}
                </div>

                {profileData?.activeBan && (
                  <div className={classes.publicBanCallout}>
                    <Typography className={classes.publicBanTitle}>
                      Banned on Ouro
                    </Typography>
                    <Typography className={classes.publicBanReason}>
                      {profileData.activeBan.reason}
                    </Typography>
                    <Typography className={classes.publicBanUntil}>
                      Until {profileData.activeBan.displayUntil}
                    </Typography>
                  </div>
                )}
                
                <div className={classes.labelSteamFaceit}>
                  {externalLinks.steamProfileUrl ? (
                    <a
                      className={classes.steamProfileLinkButton}
                      href={externalLinks.steamProfileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open Steam profile"
                    >
                      <img
                        className={classes.steamIcon}
                        src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/steam-icon-14889.png"
                      />
                    </a>
                  ) : (
                    <span
                      className={`${classes.steamProfileLinkButton} ${classes.externalProfileLinkDisabled}`}
                      aria-hidden="true"
                    >
                      <img
                        className={classes.steamIcon}
                        src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/steam-icon-14889.png"
                      />
                    </span>
                  )}
                  {externalLinks.faceitProfileUrl ? (
                    <a
                      className={classes.faceitProfileLinkButton}
                      href={externalLinks.faceitProfileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open FACEIT profile"
                    >
                      <img
                        className={classes.faceitIcon}
                        src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/faceit-seeklogo.svg"
                      />
                    </a>
                  ) : (
                    <span
                      className={`${classes.faceitProfileLinkButton} ${classes.externalProfileLinkDisabled}`}
                      aria-hidden="true"
                    >
                      <img
                        className={classes.faceitIcon}
                        src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/faceit-seeklogo.svg"
                      />
                    </span>
                  )}
                </div>
              </div>
              
              <div className={classes.labelPlayerWinRate}>
                  <div className={classes.containerMatches}>
                    <Typography className={classes.textMatches}>Matches</Typography>
                    <Typography className={classes.subText}>{matchSummary?.totalPlayedMatches || 0}</Typography>
                  </div>
                  <div className={classes.containerWinrate}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                      <Typography className={classes.textWinLoss}>Win/Loss</Typography>
                      <Typography className={classes.subText}>
                        {matchSummary?.totalMatchesWon || 0}/{matchSummary?.totalMatchesLost || 0}
                      </Typography>
                    </div>
                    <div className={classes.formatCircularProgress}>
                      <Box className={classes.boxCircularProgress} >
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          style={{ color: '#1C1E20', position: 'absolute', }}
                          size={64}
                          thickness={4.5}
                        />
                        <CircularProgress
                          variant="determinate"
                          className={classes.circularProgress}
                          value={matchSummary?.winRate || 0}
                          style={{ color: '#E8A33D' }}
                          size={64}
                          thickness={4.5}
                        />
                        <Box
                          top={0}
                          left={0}
                          bottom={0}
                          right={0}
                          position="absolute"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Typography variant="caption" component="div" style={{ color: '#ffffff', fontSize: "16px", fontWeight: 600 }}>
                            <Tooltip 
                              title="Winrate" 
                              placement="top"
                              classes={{
                                tooltip: classes.tooltipContent,
                              }}>
                              <span>{matchSummary?.winRate || 0}%</span>
                            </Tooltip>
                          </Typography>
                        </Box>
                      </Box>
                    </div>
                  </div>
              </div>
            </div>
          </div>
          
          {/* Stats Bar: Rating 3.0, ADR, Trust Score, Reports */}
          <div className={classes.containerStatsBar}>
            {/* Rating 3.0 */}
            <div className={classes.statCard}>
              <Typography className={classes.statLabel}>Rating 3.0</Typography>
              <Typography className={classes.statValue} component="div">
                <Rating3Badge
                  value={profileData?.rating3?.current ?? null}
                  size="medium"
                />
              </Typography>
              {profileData?.rating3?.peak != null && (
                <Typography style={{ fontSize: "0.65rem", color: "#666", marginTop: "0.15rem" }}>
                  Peak: {profileData.rating3.peak.toFixed(2)}
                </Typography>
              )}
            </div>

            {/* Average ADR (last 3 months) */}
            <div className={classes.statCard}>
              <Typography className={classes.statLabel}>Avg ADR (3mo)</Typography>
              <Typography className={classes.statValue} component="div">
                {recentStats ? (
                  <>
                    <BarChartIcon style={{ fontSize: "1.1rem", color: "#4CAF50" }} />
                    <span style={{ fontSize: "1rem", fontWeight: 600, color: "#ffffff" }}>
                      {recentStats?.adr || '-'}
                    </span>
                    {statsTrends?.adr && (
                      <TrendIndicator
                        value={statsTrends.adr.change}
                        direction={statsTrends.adr.direction}
                      />
                    )}
                  </>
                ) : (
                  <CircularProgress size={18} thickness={4} style={{ color: "#4CAF50" }} />
                )}
              </Typography>
            </div>
            
            {/* Trust Score - Clickable */}
            <Tooltip 
              title={reputation ? "Click to view detailed reputation breakdown" : ""}
              placement="top"
              classes={{ tooltip: classes.tooltipContent }}
            >
              <div 
                className={`${classes.statCard} ${reputation ? classes.clickableStatCard : ''}`}
                onClick={reputation ? toggleReputationDetails : undefined}
                style={{ cursor: reputation ? 'pointer' : 'default' }}
              >
                <Typography className={classes.statLabel}>Reputation</Typography>
                <Typography className={classes.statValue} component="div">
                  {reputation === null ? (
                    <CircularProgress size={18} thickness={4} style={{ color: "#E8A33D" }} />
                  ) : reputation ? (
                    <>
                      <VerifiedUserIcon style={{ fontSize: "1.1rem", color: reputation.tierColor }} />
                      <span style={{ color: reputation.tierColor, fontSize: "1rem", fontWeight: 600 }}>
                        {reputation.overallScore}/100
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: "0.8rem", color: "#666", fontWeight: 500 }}>Not Available</span>
                  )}
                </Typography>
              </div>
            </Tooltip>
            
            {/* Reports (last 3 months) */}
            {reportsSummary?.totalReports > 0 && reportsSummary?.percentages ? (
              <Tooltip
                title={
                  <div style={{ padding: '4px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>Report Breakdown:</div>
                    {(reportsSummary?.percentages?.cheating || 0) > 0 && <div>Cheating: {reportsSummary.percentages.cheating}%</div>}
                    {(reportsSummary?.percentages?.griefing || 0) > 0 && <div>Griefing: {reportsSummary.percentages.griefing}%</div>}
                    {(reportsSummary?.percentages?.smurfing || 0) > 0 && <div>Smurfing: {reportsSummary.percentages.smurfing}%</div>}
                    {(reportsSummary?.percentages?.abusive_comms || 0) > 0 && <div>Abusive Comms: {reportsSummary.percentages.abusive_comms}%</div>}
                    {(reportsSummary?.percentages?.not_team_player || 0) > 0 && <div>Not Team Player: {reportsSummary.percentages.not_team_player}%</div>}
                    {(reportsSummary?.percentages?.non_communicative || 0) > 0 && <div>Non-Communicative: {reportsSummary.percentages.non_communicative}%</div>}
                  </div>
                }
                placement="top"
                classes={{ tooltip: classes.tooltipContent }}
              >
                <div className={classes.statCard}>
                  <Typography className={classes.statLabel}>Reports (3mo)</Typography>
                  <Typography className={classes.statValue} component="div">
                    <FlagIcon style={{ fontSize: "1.1rem", color: "#f44336" }} />
                    <span style={{ fontSize: "1rem", fontWeight: 600, color: "#f44336" }}>
                      {reportsSummary?.totalReports || 0}
                    </span>
                  </Typography>
                </div>
              </Tooltip>
            ) : (
              <div className={classes.statCard}>
                <Typography className={classes.statLabel}>Reports (3mo)</Typography>
                <Typography className={classes.statValue} component="div">
                  {reportsSummary === null ? (
                    <CircularProgress size={18} thickness={4} style={{ color: "#f44336" }} />
                  ) : reportsSummary?.totalReports === 0 ? (
                    <>
                      <FlagIcon style={{ fontSize: "1.1rem", color: "#4CAF50" }} />
                      <span style={{ fontSize: "1rem", fontWeight: 600, color: "#4CAF50" }}>0</span>
                    </>
                  ) : (
                    <span style={{ fontSize: "0.8rem", color: "#666", fontWeight: 500 }}>Not Available</span>
                  )}
                </Typography>
              </div>
            )}
          </div>

          {/* Expandable Reputation Details */}
          {showReputationDetails && reputation && (
            <div className={classes.reputationDetailsContainer}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <Typography style={{ fontSize: "1.2rem", fontWeight: 600, color: "#ffffff" }}>
                  Reputation Breakdown
                </Typography>
                <Button 
                  onClick={toggleReputationDetails}
                  style={{ color: "#E8A33D", minWidth: "auto" }}
                >
                  <CloseIcon />
                </Button>
              </div>

              {reputationDetails === null ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <CircularProgress style={{ color: "#E8A33D" }} />
                  <Typography style={{ color: "#999999", marginTop: "1rem" }}>
                    Loading reputation details...
                  </Typography>
                </div>
              ) : reputationDetails ? (
                <>
                  {/* Dimensions Grid */}
                  <div className={classes.dimensionsGrid}>
                    {Object.keys(reputationDetails.dimensions).map(key => {
                      const dimension = reputationDetails.dimensions[key];
                      return (
                        <div key={key} className={classes.dimensionCard}>
                          <div className={classes.dimensionHeader}>
                            <Typography className={classes.dimensionLabel}>
                              {dimension.label}
                            </Typography>
                            <Typography className={classes.dimensionScore}>
                              {dimension.score}/100
                            </Typography>
                          </div>
                          <div className={classes.dimensionBar}>
                            <div 
                              className={classes.dimensionBarFill} 
                              style={{ width: `${dimension.score}%` }}
                            />
                          </div>
                          <Typography style={{ fontSize: "0.7rem", color: "#666", marginTop: "0.25rem" }}>
                            High: {dimension.allTimeHigh} • Low: {dimension.allTimeLow}
                          </Typography>
                        </div>
                      );
                    })}
                  </div>

                  {/* Scoring Events History */}
                  <div className={classes.eventsSection}>
                    <Typography className={classes.eventsTitle}>
                      Scoring Events History
                    </Typography>
                    <div className={classes.eventsGrid}>
                      <div className={classes.eventCard}>
                        <Typography className={classes.eventCount}>
                          {reputationDetails.scoringEvents.last3Months}
                        </Typography>
                        <Typography className={classes.eventLabel}>
                          Last 3 Months
                        </Typography>
                      </div>
                      <div className={classes.eventCard}>
                        <Typography className={classes.eventCount}>
                          {reputationDetails.scoringEvents.last6Months}
                        </Typography>
                        <Typography className={classes.eventLabel}>
                          Last 6 Months
                        </Typography>
                      </div>
                      <div className={classes.eventCard}>
                        <Typography className={classes.eventCount}>
                          {reputationDetails.scoringEvents.lastYear}
                        </Typography>
                        <Typography className={classes.eventLabel}>
                          Last Year
                        </Typography>
                      </div>
                      <div className={classes.eventCard}>
                        <Typography className={classes.eventCount}>
                          {reputationDetails.scoringEvents.allTime}
                        </Typography>
                        <Typography className={classes.eventLabel}>
                          All Time
                        </Typography>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <Typography style={{ textAlign: "center", color: "#999", padding: "2rem" }}>
                  Unable to load reputation details
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={classes.containerLayoutProfileActions}>
        <div className={classes.containerMatchWidget}>
          <div className={classes.statusWidget}>
            {playerStatus === 'offline' ? (
              <div className={classes.offlineDiv}>
                <Typography className={classes.statusText}>
                  <div className={classes.statusDot} style={{ backgroundColor: '#797979' }} />
                  Offline
                </Typography>
                <Typography className={classes.statusDetail}>
                  {lastSeen}
                </Typography>
              </div>
            ) : playerStatus === 'inMatch' ? (
              <div className={classes.inGameDiv}>
                <Typography className={classes.statusText}>
                  <div className={classes.statusDot} style={{ backgroundColor: '#4CAF50' }} />
                  In Match
                </Typography>
                <Typography className={classes.statusDetail}>
                  Playing now
                </Typography>
                {profileData?.activeMatchId && (
                  <Button
                    className={`${classes.actionButton} ${classes.btnMatchroom}`}
                    variant="contained"
                    component={Link}
                    to={`/match/${profileData.activeMatchId}`}
                  >
                    Matchroom
                  </Button>
                )}
              </div>
            ) : playerStatus === 'inMatchMaking' ? (
              <div className={classes.onlineDiv}>
                <Typography className={classes.statusText}>
                  <div className={classes.statusDot} style={{ backgroundColor: '#38b000' }} />
                  In Matchmaking
                </Typography>
                <Typography className={classes.statusDetail}>
                  Searching for match
                </Typography>
              </div>
            ) : (playerStatus === 'inPrivateLobby' || playerStatus === 'inYourLobby') ? (
              <div className={classes.onlineDiv}>
                <Typography className={classes.statusText}>
                  <div className={classes.statusDot} style={{ backgroundColor: '#38b000' }} />
                  In a Lobby
                </Typography>
                <Typography className={classes.statusDetail}>
                  Inviting or waiting for friends
                </Typography>
              </div>
            ) : playerStatus === 'inLobby' ? (
              <div className={classes.onlineDiv}>
                <Typography className={classes.statusText}>
                  <div className={classes.statusDot} style={{ backgroundColor: '#38b000' }} />
                  In Lobby
                </Typography>
                {/* <Typography className={classes.statusDetail}>
                </Typography> */}
              </div>
            ) : (
              <div className={classes.offlineDiv}>
                <Typography className={classes.statusText}>
                  <div className={classes.statusDot} style={{ backgroundColor: '#797979' }} />
                  Offline
                </Typography>
                <Typography className={classes.statusDetail}>
                  {lastSeen}
                </Typography>
              </div>
            )}
          </div>
        </div>
        <div className={classes.containerActions}>
          {!isOwnProfile && (
            <div className={classes.actionButtonContainer}>
              {effectiveFriendshipStatus === 'none' && (
                <Button
                  className={`${classes.actionButton} ${classes.btnAddFriend}`}
                  variant="contained"
                  disabled={!steamId || isFriendRequestSubmitting}
                  onClick={() => { sendRequestFriend(steamId); }}
                >
                  {isFriendRequestSubmitting ? (
                    <CircularProgress 
                      size={20}
                      thickness={4}
                      style={{ marginRight: '0.5rem', color: '#FFFFFF' }}
                    />
                  ) : (
                    <AddIcon style={{ marginRight: '0.5rem', color: '#ffffff' }} />
                  )}
                  {isFriendRequestSubmitting ? 'Sending...' : 'Add Friend'}
                </Button>
              )}
              {effectiveFriendshipStatus === 'pending_outgoing' && (
                <Button
                  className={`${classes.actionButton} ${classes.btnRequestPending}`}
                  variant="contained"
                  onClick={() => {
                    if (cancelFriendRequest && steamId) {
                      cancelFriendRequest(steamId);
                    }
                  }}
                >
                  Cancel Request
                </Button>
              )}
              {effectiveFriendshipStatus === 'pending_incoming' && (
                <div className={classes.dualActionRow}>
                  <Button
                    className={`${classes.actionButton} ${classes.btnAcceptFriend}`}
                    variant="contained"
                    onClick={() => {
                      if (acceptFriendRequest && steamId) {
                        acceptFriendRequest(steamId);
                      }
                    }}
                  >
                    <CheckIcon style={{ marginRight: '0.4rem', color: '#ffffff' }} />
                    Accept
                  </Button>
                  <Button
                    className={`${classes.actionButton} ${classes.btnDismissFriend}`}
                    variant="contained"
                    onClick={() => {
                      if (dismissFriendRequest && steamId) {
                        dismissFriendRequest(steamId);
                      }
                    }}
                  >
                    <CloseIcon style={{ marginRight: '0.4rem', color: '#ffffff' }} />
                    Dismiss
                  </Button>
                </div>
              )}
              {effectiveFriendshipStatus === 'friends' && (
                <div className={classes.dualActionRow}>
                  <Button className={`${classes.actionButton} ${classes.btnAlreadyFriend}`} variant="contained" disabled>
                    <CheckIcon style={{ marginRight: '0.5rem', color: '#ffffff' }} />
                    Friends
                  </Button>
                  <Button
                    className={`${classes.actionButton} ${classes.btnUnfriend}`}
                    variant="contained"
                    onClick={() => {
                      if (unfriend && steamId) {
                        unfriend(steamId);
                      }
                    }}
                  >
                    Unfriend
                  </Button>
                </div>
              )}
            </div>
          )}
          {!isOwnProfile && friendRequestError && (
            <div className={classes.actionButtonContainer}>
              <Typography style={{ color: "#f44336", fontSize: "0.9rem" }}>
                {friendRequestError}
              </Typography>
            </div>
          )}
          {!isOwnProfile && friendRequestSuccess && (
            <div className={classes.actionButtonContainer}>
              <Typography style={{ color: "#4CAF50", fontSize: "0.9rem" }}>
                {friendRequestSuccess}
              </Typography>
            </div>
          )}
          {isOwnProfile && (
            <div className={classes.actionButtonContainer}>
              <Button className={`${classes.actionButton} ${classes.btnSettings}`} variant="contained" component={Link} to="/account">
                <SettingsIcon style={{ marginRight: '0.5rem', color: '#ffffff' }} />
                Profile Settings
              </Button>
            </div>
          )}
          {!isOwnProfile && (
            <div className={classes.actionButtonContainer}>
              <Button className={`${classes.actionButton} ${classes.btnReport}`} variant="contained">
                <ErrorIcon style={{ marginRight: '0.5rem', color: '#ffffff' }} />
                Report
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
    );
  } catch (error) {
    console.error("❌❌❌ CRITICAL ERROR in ProfileCard render:", error);
    console.error("Error stack:", error.stack);
    console.error("ProfileCard props at error:", { profileData, matchSummary, recentStats, reportsSummary });
    return (
      <div style={{ padding: '2rem', backgroundColor: '#f44336', color: 'white', borderRadius: '4px' }}>
        <h2>Profile Card Error</h2>
        <p><strong>Error:</strong> {error.message}</p>
        <p><strong>Type:</strong> {error.name}</p>
        <pre style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '4px', fontSize: '0.85rem', overflow: 'auto' }}>
          {error.stack}
        </pre>
      </div>
    );
  }
}
